import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Button, Menu, MenuItem, SvgIcon, Tooltip, useMediaQuery } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SMALL_SCREEN_WIDTH } from 'services/constants';
import { openModal } from 'reducers/modalReducer';
import { useFetchBr } from 'hooks/queries/useFetchBr';
import { BusinessRelationshipLifecycle, BusinessRelationshipStatus } from 'views/CaseRedirect/CaseRedirect';
import { useFetchBrUserAuthorities } from 'hooks/queries/useFetchBrUserAuthorities';
import { BrUserAuthorities, BrUserAuthoritiesType } from 'types/BrTypes';

const RejectWithdrawButton = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useDispatch();
  const { brId } = useParams<{ brId: string }>();
  const { data: brDto } = useFetchBr(brId);
  const br = brDto?.businessRelationshipDto;
  const { t } = useTranslation();
  const smallScreen = useMediaQuery(`(max-width:${SMALL_SCREEN_WIDTH})`);
  let tooltipDisableBr = '';
  let tooltipRejectBr = '';
  let tooltipWithdrawBr = '';
  const { data: brAuthorities } = useFetchBrUserAuthorities();

  const checkAuthority = (authority: BrUserAuthoritiesType) => {
    return brAuthorities?.includes(authority) ? '' : `You are not eligible to perform this action.`;
  };

  const checkDisableBrStatusEligibility = () => {
    if (
      (br?.businessRelationshipStatus === BusinessRelationshipStatus.ACTIVE &&
        br?.businessRelationshipLifecycle === BusinessRelationshipLifecycle.OPERATING) ||
      (br?.businessRelationshipStatus === BusinessRelationshipStatus.APPROVED &&
        br?.businessRelationshipLifecycle === BusinessRelationshipLifecycle.ONBOARDING)
    ) {
      return true;
    }
    tooltipDisableBr += `Only BRs in Onboarding lifecycle and Approved status, or Operating lifecycle and Active status can be disabled.`;
    return false;
  };

  const checkRejectBrStatusEligibility = () => {
    if (
      (br?.businessRelationshipStatus !== BusinessRelationshipStatus.DUPLICATE_CHECK_PENDING &&
        br?.businessRelationshipLifecycle === BusinessRelationshipLifecycle.PROSPECTING) ||
      (br?.businessRelationshipStatus !== BusinessRelationshipStatus.APPROVED &&
        br?.businessRelationshipLifecycle === BusinessRelationshipLifecycle.ONBOARDING)
    ) {
      return true;
    }
    tooltipRejectBr += `Only BRs in Prospecting lifecycle (excluding Duplicate check pending status), or Onboarding lifecycle (excluding Approved status) can be rejected.`;
    return false;
  };

  const checkWithdrawBrStatusEligibility = () => {
    if (
      (br?.businessRelationshipStatus !== BusinessRelationshipStatus.DUPLICATE_CHECK_PENDING &&
        br?.businessRelationshipLifecycle === BusinessRelationshipLifecycle.PROSPECTING) ||
      br?.businessRelationshipLifecycle === BusinessRelationshipLifecycle.ONBOARDING
    ) {
      return true;
    }
    tooltipWithdrawBr += `Only BRs in Prospecting lifecycle (excluding Duplicate check pending status), or Onboarding lifecycle can be withdrawn.`;
    return false;
  };
  const isDisableStatusEnabled = checkDisableBrStatusEligibility();
  const isRejectStatusEnabled = checkRejectBrStatusEligibility();
  const isWithdrawStatusEnabled = checkWithdrawBrStatusEligibility();
  const disableAuthText = checkAuthority(BrUserAuthorities.BR_DEACTIVATE);
  const rejectWithdrawAuthText = checkAuthority(BrUserAuthorities.BR_REJECT_WITHDRAW);

  return (
    <>
      {smallScreen ? (
        <SvgIcon
          onClick={(event: any) => {
            setAnchorEl(event.currentTarget);
          }}
          color="primary"
        >
          <MoreVertIcon />
        </SvgIcon>
      ) : (
        <Button
          id="br-more-button"
          aria-haspopup="true"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
          }}
        >
          <MoreVertIcon style={{ maxHeight: '15px', marginLeft: '-10px' }} />
          {t('accounts.tabs.more.title', 'More')}
        </Button>
      )}

      <Menu
        id="br-more-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Tooltip
          title={`${tooltipRejectBr}${rejectWithdrawAuthText.trim()}`}
          arrow
          placement="left-start"
          id="reject-br-tooltip"
        >
          <span id="reject-br-span">
            <MenuItem
              disabled={!!rejectWithdrawAuthText || !isRejectStatusEnabled}
              id="menuitem-reject"
              onClick={async () => {
                dispatch(
                  openModal({
                    modalType: 'RejectWithdrawBrModal',
                    modalProps: { brId, modalPurpose: 'REJECT' },
                  }),
                );
                setAnchorEl(null);
              }}
            >
              <>
                {t('accounts.tabs.more.reject', 'Reject (Bank PoV)')}
                {(!!rejectWithdrawAuthText || !isRejectStatusEnabled) && (
                  <InfoOutlinedIcon style={{ maxHeight: '15px' }} />
                )}
              </>
            </MenuItem>
          </span>
        </Tooltip>

        <Tooltip
          title={`${tooltipWithdrawBr}${rejectWithdrawAuthText.trim()}`}
          arrow
          placement="left-start"
          id="reject-br-tooltip"
        >
          <span id="reject-br-span">
            <MenuItem
              disabled={!!rejectWithdrawAuthText || !isWithdrawStatusEnabled}
              id="menuitem-reject"
              onClick={async () => {
                dispatch(
                  openModal({
                    modalType: 'RejectWithdrawBrModal',
                    modalProps: { brId, modalPurpose: 'WITHDRAW' },
                  }),
                );
                setAnchorEl(null);
              }}
            >
              <>
                {t('accounts.tabs.more.withdraw', 'Withdraw (Partner PoV)')}
                {(!!rejectWithdrawAuthText || !isWithdrawStatusEnabled) && (
                  <InfoOutlinedIcon style={{ maxHeight: '15px' }} />
                )}
              </>
            </MenuItem>
          </span>
        </Tooltip>

        <Tooltip
          title={`${tooltipDisableBr}${disableAuthText.trim()}`}
          arrow
          placement="left-start"
          id="deactivate-br-tooltip"
        >
          <span id="deactivate-br-span">
            <MenuItem
              disabled={!isDisableStatusEnabled || !!disableAuthText}
              id="menuitem-deactivate"
              onClick={async () => {
                dispatch(
                  openModal({
                    modalType: 'DisableBrModal',
                    modalProps: { brId },
                  }),
                );
                setAnchorEl(null);
              }}
            >
              <>
                {t('accounts.tabs.more.deactivate', 'Deactivate BR')}
                {(!isDisableStatusEnabled || !!disableAuthText) && <InfoOutlinedIcon style={{ maxHeight: '15px' }} />}
              </>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
};

export default RejectWithdrawButton;
